import { createTheme } from '@material-ui/core/styles';

/**
 * Institutional design tokens. Kept in sync with src/styles/global.css so that
 * MUI components and plain CSS share one palette.
 */
export const tokens = {
    blue900: '#0e2748',
    blue800: '#143a66',
    blue700: '#1b4779',
    blue600: '#225491',
    blue500: '#2f6db5',
    blue100: '#d8e5f3',
    blue50: '#eef4fa',
    gold: '#c8a02a',
    goldSoft: '#f4e7c0',
    red: '#a62121',
    green: '#1f7a55',
    ink: '#0f172a',
    inkSoft: '#334155',
    muted: '#5b6878',
    border: '#e2e8f0',
    borderStrong: '#cbd5e1',
    surface: '#f6f8fb',
    white: '#ffffff',
    radius: 10,
    shadowSm: '0 1px 2px rgba(15, 23, 42, 0.06)',
    shadowMd: '0 4px 16px rgba(15, 23, 42, 0.08)',
    shadowLg: '0 16px 40px rgba(15, 23, 42, 0.12)',
    headingFont: '"Sora", "Inter", "Helvetica Neue", Arial, sans-serif',
    bodyFont: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
};

const theme = createTheme({
    palette: {
        type: 'light',
        primary: {
            main: tokens.blue600,
            dark: tokens.blue900,
            light: tokens.blue500,
            contrastText: '#ffffff',
        },
        secondary: {
            main: tokens.blue800,
            dark: tokens.blue900,
            light: tokens.blue100,
            contrastText: '#ffffff',
        },
        error: { main: tokens.red },
        success: { main: tokens.green },
        warning: { main: tokens.gold },
        info: { main: tokens.blue500 },
        divider: tokens.border,
        background: {
            default: tokens.white,
            paper: tokens.white,
        },
        text: {
            primary: tokens.ink,
            secondary: tokens.muted,
        },
    },
    shape: {
        borderRadius: tokens.radius,
    },
    typography: {
        fontFamily: tokens.bodyFont,
        htmlFontSize: 16,
        h1: {
            fontFamily: tokens.headingFont,
            fontWeight: 700,
            fontSize: '2.75rem',
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontFamily: tokens.headingFont,
            fontWeight: 700,
            fontSize: '2.125rem',
            lineHeight: 1.2,
            letterSpacing: '-0.015em',
        },
        h3: {
            fontFamily: tokens.headingFont,
            fontWeight: 600,
            fontSize: '1.75rem',
            lineHeight: 1.25,
        },
        h4: {
            fontFamily: tokens.headingFont,
            fontWeight: 600,
            fontSize: '1.4375rem',
            lineHeight: 1.3,
        },
        h5: {
            fontFamily: tokens.headingFont,
            fontWeight: 600,
            fontSize: '1.1875rem',
            lineHeight: 1.35,
        },
        h6: {
            fontFamily: tokens.headingFont,
            fontWeight: 600,
            fontSize: '1.0625rem',
            lineHeight: 1.4,
        },
        subtitle1: { fontSize: '1.0625rem', lineHeight: 1.6 },
        body1: { fontSize: '1rem', lineHeight: 1.65 },
        body2: { fontSize: '0.9375rem', lineHeight: 1.6 },
        caption: { fontSize: '0.8125rem', lineHeight: 1.5 },
        overline: {
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
        },
        button: {
            fontFamily: tokens.bodyFont,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9375rem',
            letterSpacing: 0,
        },
    },
    overrides: {
        MuiCssBaseline: {
            '@global': {
                body: {
                    backgroundColor: tokens.white,
                },
            },
        },
        MuiButton: {
            root: {
                padding: '9px 20px',
                borderRadius: 6,
            },
            contained: {
                boxShadow: 'none',
                '&:hover': { boxShadow: tokens.shadowSm },
            },
            containedPrimary: {
                backgroundColor: tokens.blue600,
                '&:hover': { backgroundColor: tokens.blue800 },
            },
            outlined: {
                borderColor: tokens.borderStrong,
            },
            outlinedPrimary: {
                borderColor: tokens.blue600,
                '&:hover': {
                    borderColor: tokens.blue800,
                    backgroundColor: tokens.blue50,
                },
            },
        },
        MuiPaper: {
            rounded: { borderRadius: tokens.radius },
            elevation1: { boxShadow: tokens.shadowSm },
        },
        MuiCard: {
            root: {
                borderRadius: tokens.radius,
                border: `1px solid ${tokens.border}`,
                boxShadow: 'none',
            },
        },
        MuiChip: {
            root: {
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 26,
            },
            outlined: {
                borderColor: tokens.borderStrong,
            },
        },
        MuiOutlinedInput: {
            root: {
                backgroundColor: tokens.white,
                borderRadius: 6,
            },
            notchedOutline: {
                borderColor: tokens.borderStrong,
            },
        },
        MuiTab: {
            root: {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9375rem',
            },
        },
        MuiTableCell: {
            head: {
                fontWeight: 700,
                color: tokens.ink,
                backgroundColor: tokens.blue50,
            },
        },
        MuiLink: {
            root: {
                color: tokens.blue600,
            },
        },
        MuiBreadcrumbs: {
            separator: {
                color: tokens.borderStrong,
            },
        },
    },
    props: {
        MuiButtonBase: { disableRipple: false },
        MuiCard: { elevation: 0 },
    },
});

export default theme;
